import React, { useState } from "react";
import { makeGETRequestOnUserService } from "../Api/api";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { LoginUser } from "../pages/WhatsappCampaign/Interfaces";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ mt: 2 }}>{children}</Box>}
  </div>
);

interface ToastProps {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

const DateRangePicker: React.FC<{
  fromDate: string;
  toDate: string;
  onChange: (type: "from" | "to", value: string) => void;
}> = ({ fromDate, toDate, onChange }) => (
  <Grid container spacing={3} sx={{ mb: 2, mt: 1 }}>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        type="date"
        label="From Date"
        InputLabelProps={{ shrink: true }}
        value={fromDate}
        onChange={(e) => onChange("from", e.target.value)}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        type="date"
        label="To Date"
        InputLabelProps={{ shrink: true }}
        value={toDate}
        onChange={(e) => onChange("to", e.target.value)}
      />
    </Grid>
  </Grid>
);

const Reports: React.FC = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastProps>({
    open: false,
    message: "",
    severity: "success",
  });

  const loginUser: LoginUser = JSON.parse(
    localStorage.getItem("loginUser") || ""
  );
  
  const resetForm = () => {
    setFromDate("");
    setToDate("");
    setError("");
  };

  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    resetForm();
  };

  const validateDates = (type: "from" | "to", value: string) => {
    if (type === "from") setFromDate(value);
    else setToDate(value);

    if (type === "from" && toDate && value > toDate) {
      setError("From Date cannot be greater than To Date.");
    } else if (type === "to" && fromDate && value < fromDate) {
      setError("To Date cannot be earlier than From Date.");
    } else {
      setError("");
    }
  };

  const handleDownload = async () => {
    if (!fromDate || !toDate) {
      setError("Please select both From Date and To Date.");
      return;
    }
    if (error) return;

    setLoading(true);

    try {
      const reportTypes = ["client", "piq", "order"];
      const reportType = reportTypes[tabValue];
      const endpoints: Record<string, string> = {
        client: `/user/client_information/?from_date=${fromDate}&to_date=${toDate}`,
        piq: `/user/piq_table/?from_date=${fromDate}&to_date=${toDate}`,
        order: `/order_management_v2/order_csv_download/?from_date=${fromDate}&to_date=${toDate}`,
      };

      const response = await makeGETRequestOnUserService(endpoints[reportType]);

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;

      const formatDate = (d: string) => {
        const [year, month, day] = d.split("-");
        return `${day}-${month}-${year}`;
      };

      let filenameMap: Record<string, string> = {
        client: `Internal Audit Client Information Report from ${formatDate(
          fromDate
        )} to ${formatDate(toDate)}.csv`,
        piq: `PIQ Table 18 Report from ${formatDate(fromDate)} to ${formatDate(
          toDate
        )}.csv`,
        order: `Orders Report from ${formatDate(
          fromDate
        )} to ${formatDate(toDate)}.csv`,
      };

      let filename = filenameMap[reportType];

      const contentDisposition = response.headers?.["content-disposition"];
      const match = contentDisposition?.match(/filename="(.+)"/);
      if (match?.[1]) filename = match[1];

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setToast({
        open: true,
        message: "Report downloaded successfully!",
        severity: "success",
      });
    } catch (error: any) {
      console.error("CSV download failed:", error);

      if (error.response?.status === 404) {
        setToast({
          open: true,
          message: "No records found for given date range.",
          severity: "error",
        });
      } else {
        setToast({
          open: true,
          message: "Failed to download CSV file. Please try again.",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper
        elevation={4}
        sx={{
          p: 3,
          mt: 2,
          borderRadius: 3,
          backgroundColor: "#fafafa",
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          📊 Reports
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Tabs value={tabValue} onChange={handleChangeTab}>
          <Tab
            label="Internal Audit Client Information"
            sx={{
              textTransform: "none",
              borderRadius: "25px",
              px: 2,
              py: 1,
              minHeight: "36px",
              fontWeight: "bold",
              "&.Mui-selected": {
                backgroundColor: "#90c7faff",
                color: "#000000ff",
              },
            }}
          />
          <Tab
            label="PIQ Table 18"
            sx={{
              textTransform: "none",
              borderRadius: "25px",
              px: 2,
              py: 1,
              minHeight: "36px",
              fontWeight: "bold",
              "&.Mui-selected": {
                backgroundColor: "#90c7faff",
                color: "#000000ff",
              },
            }}
          />
          {loginUser?.isSuperuser && (
            <Tab
            label="RFQ Orders"
            sx={{
              textTransform: "none",
              borderRadius: "25px",
              px: 2,
              py: 1,
              minHeight: "36px",
              fontWeight: "bold",
              "&.Mui-selected": {
                backgroundColor: "#90c7faff",
                color: "#000000ff",
              },
            }}
          />
          )}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onChange={validateDates}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onChange={validateDates}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onChange={validateDates}
          />
        </TabPanel>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {fromDate && toDate && !error && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ py: 1.5 }}
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Download Report"
            )}
          </Button>
        )}
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Reports;
